// 特殊権限フラグ (UserTenant.specialFlags) の正準レジストリ。
// 機能フラグ (lib/featureFlags) と同様、ここを single source of truth とし、
// 付与UIの列挙・保存時の正規化(未知/撤去フラグの自己修復)・各 consumer のキー参照を
// 一元化する。フラグの追加/撤去はこのファイルだけを編集すればよい。

export interface SpecialFlagDefinition {
  /** DDB UserTenant.specialFlags に保存されるキー (camelCase 統一) */
  key: string;
  /** 付与UIのラベル */
  label: string;
  /** 付与UIの説明 */
  description: string;
  /** Umeee 社内テナントでのみ意味を持つ (付与UIは Umeee のときだけ表示) */
  umeeeOnly?: boolean;
}

/** 正準レジストリ (キー = 保存値)。順序が付与UI・正規化の正準順になる。 */
export const SPECIAL_FLAGS = {
  sysadmin: {
    key: "sysadmin",
    label: "システム管理者",
    description: "全ての管理者機能にアクセスできます",
  },
  bypassIpRestriction: {
    key: "bypassIpRestriction",
    label: "IP制限バイパス",
    description: "IP制限を受けずにアクセスできます",
  },
  showRawTranscript: {
    key: "showRawTranscript",
    label: "音声ログ表示",
    description: "商談の音声ログ（生データ）を追加表示できます",
  },
  agenticUser: {
    key: "agenticUser",
    label: "エージェンティックAI",
    // PRODUCT_DEV-653: 「開発量」画面もこのフラグでゲートしている
    // （API の denyIfNotAgenticUser と UI の useRequireSpecialFlag の両方）。
    // 説明文がそれを書いていないと「開発量が見えない」ときの手掛かりが無くなる。
    description:
      "Adminの「エージェンティック ジョブ」「開発量」メニューを解放します。",
    umeeeOnly: true,
  },
  pipelineApprover: {
    key: "pipelineApprover",
    label: "リリース承認",
    description: "AdminからCodePipelineのリリース承認/却下ができます。",
    umeeeOnly: true,
  },
} as const;

export type SpecialFlagKey = keyof typeof SPECIAL_FLAGS;

/** 付与UI列挙用の配列 (正準順)。 */
export const SPECIAL_FLAG_LIST: SpecialFlagDefinition[] =
  Object.values(SPECIAL_FLAGS);

/** 既知フラグキー集合 (正準順)。 */
export const SPECIAL_FLAG_KEYS: string[] = Object.keys(SPECIAL_FLAGS);

/**
 * 保存時正規化: 未知/撤去済みフラグを落とし、正準順・重複なしの配列にする。
 * registry から外したフラグは、そのフラグを保有するユーザーの「次回保存」で
 * 自己修復的に掃除される (機能フラグの正規化と同じ思想)。未知フラグを 400 で
 * 拒否せず黙って落とすことで、撤去フラグ保有者の保存がブロックされない。
 */
export function normalizeSpecialFlags(
  flags: string[] | undefined | null,
): string[] {
  if (!flags || flags.length === 0) return [];
  const present = new Set(flags);
  return SPECIAL_FLAG_KEYS.filter((k) => present.has(k));
}
